import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { url } from "../../../../env";
import { AppTableCard, AppTableEmptyState, AppTablePagination } from "../../../../Components/TableKit";

/** Avatar color palette based on name hash */
const AVATAR_COLORS = [
    { bg: "#dbeafe", color: "#2563eb" },
    { bg: "#f3e8ff", color: "#7c3aed" },
    { bg: "#ccfbf1", color: "#0d9488" },
    { bg: "#ffedd5", color: "#ea580c" },
    { bg: "#fce7f3", color: "#db2777" },
    { bg: "#e0f2fe", color: "#0284c7" },
];

function getInitials(name = "") {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join("");
}

function getAvatarColor(name = "") {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function timeAgo(dateStr) {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `Hace ${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Hace ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `Hace ${days}d`;
    const months = Math.floor(days / 30);
    return `Hace ${months} mes${months > 1 ? "es" : ""}`;
}

const ITEMS_PER_PAGE = 10;

function stepBadgeClass(paso) {
    switch (paso) {
        case "Faltan documentos básicos": return "missing";
        case "Análisis": return "analysis";
        case "En proceso visita ocular": return "visit";
        case "En proceso cotización": return "quote";
        default: return "none";
    }
}

export const SelectedPersonComponent = ({ setSelectedPerson, setPeople, setLoading: setParentLoading }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [filter, setFilter] = useState("all"); // "all" | "PF" | "PM"
    const [page, setPage] = useState(1); // 1-based para el backend
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: ITEMS_PER_PAGE, total: 0 });
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const searchTimer = useRef(null);

    // Cache de páginas: evita peticiones repetidas
    const pageCache = useRef({}); // key: "filter|search|page" -> { data, pagination }
    // Acumula todas las personas cargadas para búsqueda local instantánea
    const allLoadedPeople = useRef([]);

    const getCacheKey = (p, search, tipo) => `${tipo}|${search}|${p}`;

    const mergeIntoPeopleCache = (newPeople) => {
        const ids = new Set(allLoadedPeople.current.map((p) => p.id));
        newPeople.forEach((p) => {
            if (!ids.has(p.id)) {
                allLoadedPeople.current.push(p);
            }
        });
    };

    // Mapea filtro de subtype a tipo_persona para el backend
    const getServerTipo = (tipo) => (tipo === "PFAE" || tipo === "PF") ? "PF" : tipo;

    const fetchPeople = useCallback(async (p, search, tipo, { silent = false } = {}) => {
        const key = getCacheKey(p, search, tipo);

        // Si ya tenemos esta página en cache, usar datos guardados
        if (pageCache.current[key]) {
            const cached = pageCache.current[key];
            setData(cached.data);
            setPagination(cached.pagination);
            setPeople?.(cached.data);
            return;
        }

        if (silent) {
            setSearching(true);
        } else {
            setLoading(true);
            setParentLoading?.(true);
        }
        try {
            const serverTipo = getServerTipo(tipo);
            const response = await axios.get(`${url}/getUsersReport`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                params: {
                    page: p,
                    per_page: ITEMS_PER_PAGE,
                    search: search || "",
                    tipo_persona: serverTipo || "all",
                },
            });
            const res = response.data;
            if (res.status === "success") {
                const serverItems = res.data || [];
                // Acumular personas para búsqueda local
                mergeIntoPeopleCache(serverItems);

                // Filtrar por persona_subtype si es PFAE o PF
                const subtypeFilter = (items) => {
                    if (tipo === "PFAE" || tipo === "PF") {
                        return items.filter((p) => p.persona_subtype === tipo);
                    }
                    return items;
                };

                // Si hay búsqueda, combinar resultados del server + locales
                let finalItems = subtypeFilter(serverItems);
                if (search && search.trim()) {
                    const q = search.toLowerCase();
                    const localMatches = allLoadedPeople.current.filter(
                        (p) =>
                            (p.name?.toLowerCase().includes(q) || String(p.id).includes(q)) &&
                            (tipo === "all" || p.persona_subtype === tipo)
                    );
                    // Server primero, luego agregar coincidencias locales que no vinieron del server
                    const serverIds = new Set(finalItems.map((p) => p.id));
                    const extraLocal = localMatches.filter((p) => !serverIds.has(p.id));
                    finalItems = [...finalItems, ...extraLocal];
                }

                // Guardar en cache
                pageCache.current[key] = { data: finalItems, pagination: res.pagination };

                setData(finalItems);
                setPagination(res.pagination);
                setPeople?.(serverItems);
            }
        } catch (error) {
            console.error("Error al obtener datos:", error);
        } finally {
            if (silent) {
                setSearching(false);
            } else {
                setLoading(false);
                setParentLoading?.(false);
            }
        }
    }, [setPeople, setParentLoading]);

    // Fetch on mount
    useEffect(() => {
        fetchPeople(1, "", "all");
    }, [fetchPeople]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchPeople(newPage, searchQuery, filter);
    };

    const handleFilterChange = (f) => {
        setFilter(f);
        setPage(1);
        fetchPeople(1, searchQuery, f);
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchQuery(val);

        // Búsqueda local instantánea en datos ya cargados
        if (val.trim()) {
            const q = val.toLowerCase();
            const localMatches = allLoadedPeople.current.filter(
                (p) =>
                    (p.name?.toLowerCase().includes(q) || String(p.id).includes(q)) &&
                    (filter === "all" || p.persona_subtype === filter)
            );
            setData(localMatches.slice(0, ITEMS_PER_PAGE));
            setPagination((prev) => ({
                ...prev,
                total: localMatches.length,
                current_page: 1,
                last_page: Math.max(1, Math.ceil(localMatches.length / ITEMS_PER_PAGE)),
            }));
        }

        // Búsqueda completa al backend con debounce (silent: no muestra loader grande)
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setPage(1);
            // Limpiar cache de búsquedas anteriores para obtener resultados frescos
            const keysToRemove = Object.keys(pageCache.current).filter((k) => k.startsWith(`${filter}|${val}|`));
            keysToRemove.forEach((k) => delete pageCache.current[k]);
            fetchPeople(1, val, filter, { silent: true });
        }, 600);
    };

    const handleClearSearch = () => {
        setSearchQuery("");
        clearTimeout(searchTimer.current);
        setPage(1);
        fetchPeople(1, "", filter, { silent: true });
    };

    return (
        <div className="sp-container">
            <style>{spStyles}</style>

            {/* Title */}
            <motion.div
                className="sp-header"
                initial={{ opacity: 0, y: -15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 280, damping: 24 }}
            >
                <h1 className="sp-title">Bienvenido</h1>
                <p className="sp-subtitle">
                    Aquí podrás administrar a todas las personas y sus reportes de manera eficiente
                </p>
            </motion.div>

            {/* Search + Filters (same row) */}
            <motion.div
                className="sp-toolbar"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.06, type: "spring", stiffness: 300, damping: 24 }}
            >
                <div className="sp-search-box">
                    <i className={`pi ${searching ? "pi-spin pi-spinner" : "pi-search"} sp-search-icon`} />
                    <input
                        className="sp-search-input"
                        type="text"
                        placeholder="Buscar personas, reportes o ID..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        autoFocus
                    />
                    {searchQuery && (
                        <button className="sp-search-clear" onClick={handleClearSearch}>
                            <i className="pi pi-times" />
                        </button>
                    )}
                </div>
                <div className="sp-filters">
                    {[
                        { key: "all", label: "Todo" },
                        { key: "PFAE", label: "Física AE" },
                        { key: "PF", label: "Física" },
                        { key: "PM", label: "Morales" },
                    ].map((f) => (
                        <button
                            key={f.key}
                            className={`sp-filter-btn ${filter === f.key ? "active" : ""}`}
                            onClick={() => handleFilterChange(f.key)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </motion.div>

            {/* Table */}
            <AppTableCard
                delay={0.15}
                footer={
                    <AppTablePagination
                        page={page}
                        lastPage={pagination.last_page}
                        total={pagination.total}
                        perPage={pagination.per_page}
                        loading={loading}
                        onPageChange={handlePageChange}
                    />
                }
            >
                <table className="tk-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Tipo</th>
                            <th>Tipo de plan</th>
                            <th>Paso Actual</th>
                            <th>Última Actividad</th>
                            <th className="tk-th-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <AppTableEmptyState colSpan={6} loading />
                        ) : data.length > 0 ? (
                            data.map((person) => {
                                const avatarColor = getAvatarColor(person.name);
                                return (
                                    <tr
                                        key={person.id}
                                        className="tk-row"
                                        onClick={() => setSelectedPerson(person)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td>
                                            <div className="sp-name-cell">
                                                <div
                                                    className="sp-avatar"
                                                    style={{ background: avatarColor.bg, color: avatarColor.color }}
                                                >
                                                    {getInitials(person.name)}
                                                </div>
                                                <div>
                                                    <div className="sp-person-name">{person.name}</div>
                                                    <div className="sp-person-id">ID: #{person.id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`sp-badge ${person.persona_subtype === "PFAE" ? "pfae" : person.persona_subtype === "PF" ? "pf" : "pm"}`}>
                                                {person.persona_subtype === "PFAE" ? "Física AE" : person.persona_subtype === "PF" ? "Física" : "Moral"}
                                            </span>
                                        </td>
                                        <td>
                                            {person.plan_tipo ? (
                                                <span className={`sp-plan-badge ${person.plan_tipo === "Puro" ? "puro" : "financiero"}`}>
                                                    {person.plan_tipo}
                                                </span>
                                            ) : (
                                                <span className="sp-plan-badge none">—</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`sp-step-badge ${stepBadgeClass(person.paso_actual)}`}>
                                                {person.paso_actual || "—"}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="sp-date-cell">
                                                <span className="sp-date-main">
                                                    {person.fecha_generacion
                                                        ? new Date(person.fecha_generacion).toLocaleDateString("es-MX", {
                                                            day: "2-digit",
                                                            month: "short",
                                                            year: "numeric",
                                                        })
                                                        : "Sin fecha"}
                                                </span>
                                                <span className="sp-date-ago">
                                                    {timeAgo(person.fecha_generacion)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="tk-td-right">
                                            <button
                                                className="tk-action-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedPerson(person);
                                                }}
                                                title="Ver reporte"
                                            >
                                                <i className="pi pi-eye" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <AppTableEmptyState colSpan={6} />
                        )}
                    </tbody>
                </table>
            </AppTableCard>
        </div>
    );
};

/* -- Styles (vanilla CSS, no Tailwind) -- */
const spStyles = `
.sp-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  max-width: 960px;
  margin: 0 auto;
  width: 100%;
}

/* Header */
.sp-header {
  text-align: center;
}
.sp-title {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 800;
  color: #32b8df;
  letter-spacing: -0.03em;
}
.sp-subtitle {
  margin: 0.5rem 0 0;
  font-size: 1rem;
  color: #64748b;
  font-weight: 500;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

/* Toolbar (search + filters row) */
.sp-toolbar {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
}
.sp-search-box {
  flex: 1;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  transition: border-color 0.2s, box-shadow 0.2s;
}
.sp-search-box:focus-within {
  border-color: #32b8df;
  box-shadow: 0 0 0 3px rgba(50,184,223,0.12);
}
.sp-search-icon {
  font-size: 1rem;
  color: #94a3b8;
  flex-shrink: 0;
}
.sp-search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 12px 10px;
  font-size: 0.95rem;
  color: #0f172a;
}
.sp-search-input::placeholder {
  color: #94a3b8;
}
.sp-search-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 4px;
  display: flex;
  font-size: 0.8rem;
}
.sp-search-clear:hover { color: #32b8df; }

/* Filters */
.sp-filters {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.sp-filter-btn {
  padding: 6px 16px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.18s;
}
.sp-filter-btn:hover {
  background: #f8fafc;
}
.sp-filter-btn.active {
  background: #32b8df;
  color: #fff;
  border-color: #32b8df;
}

/* Name cell */
.sp-name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sp-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.78rem;
  flex-shrink: 0;
  letter-spacing: 0.3px;
}
.sp-person-name {
  font-weight: 600;
  font-size: 0.88rem;
  color: #0f172a;
}
.sp-person-id {
  font-size: 0.72rem;
  color: #94a3b8;
  margin-top: 1px;
}

/* Badge */
.sp-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
}
.sp-badge.pfae {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fde68a;
}
.sp-badge.pf {
  background: #eef2ff;
  color: #4338ca;
  border: 1px solid #e0e7ff;
}
.sp-badge.pm {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #d1fae5;
}

/* Plan tipo (Puro / Financiero) */
.sp-plan-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
}
.sp-plan-badge.puro {
  background: #e0f2fe;
  color: #0369a1;
  border: 1px solid #bae6fd;
}
.sp-plan-badge.financiero {
  background: #f3e8ff;
  color: #6d28d9;
  border: 1px solid #e9d5ff;
}
.sp-plan-badge.none {
  background: #f1f5f9;
  color: #94a3b8;
  border: 1px solid #e2e8f0;
}

/* Paso actual */
.sp-step-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}
.sp-step-badge.missing {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}
.sp-step-badge.analysis {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fde68a;
}
.sp-step-badge.visit {
  background: #dbeafe;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}
.sp-step-badge.quote {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}
.sp-step-badge.none {
  background: #f1f5f9;
  color: #94a3b8;
  border: 1px solid #e2e8f0;
}

/* Date cell */
.sp-date-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.sp-date-main {
  font-size: 0.85rem;
  font-weight: 500;
  color: #334155;
}
.sp-date-ago {
  font-size: 0.72rem;
  color: #94a3b8;
}

/* Responsive */
@media (max-width: 640px) {
  .sp-container { padding: 1.5rem 1rem; gap: 1rem; }
  .sp-title { font-size: 1.4rem; }
  .sp-subtitle { font-size: 0.88rem; }
}
`;
